import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { gsap } from 'gsap';
import { Subject, takeUntil } from 'rxjs';
import {
    McpRealtimeStatus,
    McpTranscriptEntry,
} from 'app/core/services/mcp-realtime.service';
import { McpRealtimeRouterService } from 'app/core/services/realtime-segments/mcp-realtime-router.service';
import { PageActionService } from 'app/core/services/page-action.service';

/**
 * KioskAgentComponent
 *
 * Voice-only assistant surface for the kiosk. Unlike the desktop
 * `mcp-drawer` (voice + text + debug log), this is a full-screen, hands-free
 * overlay: tap the top-bar button to turn it on, then just talk. It drives the
 * SAME realtime session and page-action manifest as the admin assistant, so on
 * the Packages station it can receive, label, and ship exactly like an operator.
 */
@Component({
    selector: 'kiosk-agent',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './kiosk-agent.component.html',
    styleUrls: ['./kiosk-agent.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KioskAgentComponent implements OnInit, OnDestroy {
    /** Whether the overlay is shown (session on). */
    active = false;
    status: McpRealtimeStatus = 'idle';
    transcript: McpTranscriptEntry[] = [];
    aiSpeaking = false;
    micMuted = true;
    /**
     * True while the local browser mic is picking up voice-level audio.
     * Independent of any server VAD — see GrokRealtimeSegment.updateUserSpeaking.
     * Drives the green "you're being heard" pulse on the card outline.
     */
    userSpeaking = false;
    /** Raw mic input level 0..1 (peak) from the active segment. */
    inputLevel = 0;

    /**
     * Which realtime engine the kiosk runs on. Default is Gemini; the top-bar
     * toggle can flip it to Grok for testing. Applied as a runtime-only segment
     * override so the global default (everything else) is never changed.
     */
    engine: 'gemini' | 'gemini2' | 'grok' = 'gemini2';

    private readonly _destroy$ = new Subject<void>();
    private _avatarTl?: gsap.core.Timeline;

    constructor(
        private readonly _realtime: McpRealtimeRouterService,
        private readonly _cdr: ChangeDetectorRef,
        private readonly _host: ElementRef<HTMLElement>,
        private readonly _zone: NgZone,
        private readonly _dialog: MatDialog,
        private readonly _pageAction: PageActionService,
    ) {}

    ngOnInit(): void {
        this._realtime.status$
            .pipe(takeUntil(this._destroy$))
            .subscribe((s) => {
                this.status = s;
                this._cdr.markForCheck();
            });

        this._realtime.transcript$
            .pipe(takeUntil(this._destroy$))
            .subscribe((t) => {
                this.transcript = t;
                this._cdr.markForCheck();
                setTimeout(() => this._scrollToBottom(), 40);
            });

        this._realtime.aiSpeaking$
            .pipe(takeUntil(this._destroy$))
            .subscribe((s) => {
                this.aiSpeaking = s;
                this._syncPulse();
                this._cdr.markForCheck();
            });

        this._realtime.micMuted$
            .pipe(takeUntil(this._destroy$))
            .subscribe((m) => {
                this.micMuted = m;
                this._cdr.markForCheck();
            });

        this._realtime.userSpeaking$
            .pipe(takeUntil(this._destroy$))
            .subscribe((s) => {
                this.userSpeaking = s;
                this._cdr.markForCheck();
            });

        this._realtime.inputLevel$
            .pipe(takeUntil(this._destroy$))
            .subscribe((lvl) => {
                this.inputLevel = lvl;
                this._cdr.markForCheck();
            });

        // Voice "goodbye" / "that's all" — the assistant does NOT close; it
        // sleeps (mic muted, wake word armed) and stays connected. Here we just
        // tidy the UI: dismiss any open dialogs and refresh the on-page lists so
        // the station is clean for the next visitor.
        this._realtime.endRequested$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this.onVisitEnded());
    }

    /**
     * The assistant confirmed the visit is over and went to sleep. Close any
     * open dialogs/drawers and refresh the on-page lists (NOT a page reload, and
     * NOT closing the assistant — it stays listening for the wake word).
     */
    private onVisitEnded(): void {
        try {
            this._dialog.closeAll();
        } catch {}
        // Re-fetch the current page's lists via its registered `refresh`
        // page-action. Harmless no-op on pages that don't expose it.
        this._pageAction.dispatch('refresh').catch(() => {});
    }

    /** Mic level as a 0..100 bar width. Peak ~0.05 already reads as loud speech. */
    get micLevelPct(): number {
        const pct = Math.round((this.inputLevel / 0.15) * 100);
        return Math.max(0, Math.min(100, pct));
    }

    ngOnDestroy(): void {
        this._killPulse();
        if (this.active) {
            this._realtime.stop();
            this._realtime.useRuntimeSegment(null);
        }
        this._destroy$.next();
        this._destroy$.complete();
    }

    /** Whether a session is currently running (used by the top-bar button). */
    get isRunning(): boolean {
        return this.active;
    }

    /** Turn the assistant on/off. */
    toggle(): void {
        if (this.active) {
            this.deactivate();
        } else {
            this.activate();
        }
    }

    /** Open the overlay and start a hands-free (open-mic) voice session. */
    async activate(): Promise<void> {
        if (this.active) {
            return;
        }
        this.active = true;
        this._cdr.markForCheck();
        this._startPulse();
        // Kiosk-only: run on the selected engine while the agent is active.
        // Runtime override — the global default (admin, etc.) is never changed.
        this._realtime.useRuntimeSegment(this.engine);
        this._realtime.prepareKioskSession(true);
        try {
            // Land in the wake-word "asleep" state: say "Yo" to begin. Each
            // visit then ends back here (end_conversation), so start and end
            // match and the mic is never hot between visitors.
            await this._realtime.start();
        } catch {
            // start() surfaces its own status/transcript on failure.
        }
    }

    /** Stop the session and close the overlay. */
    deactivate(): void {
        if (!this.active) {
            return;
        }
        this._realtime.stop();
        this._realtime.useRuntimeSegment(null);
        this.active = false;
        this._killPulse();
        this._cdr.markForCheck();
    }

    /** Retry after a connection error without leaving the overlay. */
    async retry(): Promise<void> {
        try {
            await this._realtime.start();
        } catch {
            // no-op — status reflects the failure.
        }
    }

    /**
     * The latest thing the ASSISTANT said. We intentionally never surface the
     * user's own transcription — the side-channel ASR is often wrong and only
     * confuses the operator. Voice input is confirmed by the live meter instead.
     */
    get assistantLine(): string {
        for (let i = this.transcript.length - 1; i >= 0; i--) {
            const e = this.transcript[i];
            if (e.role === 'assistant' && e.text && e.text.trim()) {
                return e.text.trim();
            }
        }
        return '';
    }

    /** True while connected but muted — waiting for the "Yo" wake word. */
    get isAsleep(): boolean {
        return this.status === 'active' && this.micMuted;
    }

    get statusLabel(): string {
        switch (this.status) {
            case 'active':
                if (this.micMuted) return 'Say "Yo" to start';
                return this.aiSpeaking ? 'Speaking…' : 'Listening — just talk';
            case 'connecting':
                return 'Connecting…';
            case 'error':
                return "Couldn't connect — tap to retry";
            default:
                return 'Starting…';
        }
    }

    get orbState(): 'connecting' | 'speaking' | 'listening' | 'error' | 'idle' | 'asleep' {
        if (this.status === 'connecting') return 'connecting';
        if (this.status === 'error') return 'error';
        if (this.status === 'active') {
            if (this.micMuted) return 'asleep';
            return this.aiSpeaking ? 'speaking' : 'listening';
        }
        return 'idle';
    }

    private _prefersReducedMotion(): boolean {
        return (
            typeof window !== 'undefined' &&
            !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        );
    }

    private _startPulse(): void {
        if (this._prefersReducedMotion()) {
            return;
        }
        // The waveform is rendered under @if; grab it after the view updates.
        setTimeout(() => {
            const bars = this._host.nativeElement.querySelectorAll('.ka-eq__bar');
            if (!bars.length || this._avatarTl) {
                return;
            }
            this._zone.runOutsideAngular(() => {
                const tl = gsap.timeline();
                // Each bar bounces between short and tall on its own offset for a
                // lively, organic "voice" equalizer.
                tl.to(bars, {
                    scaleY: 1,
                    duration: 0.42,
                    ease: 'sine.inOut',
                    repeat: -1,
                    yoyo: true,
                    stagger: { each: 0.12, from: 'center', yoyo: true, repeat: -1 },
                    repeatRefresh: true,
                });
                this._avatarTl = tl;
                this._syncPulse();
            });
        }, 0);
    }

    /** Speed the talking up while the assistant is actually speaking. */
    private _syncPulse(): void {
        if (this._avatarTl) {
            this._avatarTl.timeScale(this.aiSpeaking ? 2.2 : 1);
        }
    }

    private _killPulse(): void {
        this._avatarTl?.kill();
        this._avatarTl = undefined;
    }

    private _scrollToBottom(): void {
        const el = this._host.nativeElement.querySelector('.ka-transcript');
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }
}
